import { useLogin, useUser } from '@innovamat/ga-features';
import { useGetAtlasDemoStudentEmailMutation } from '@innovamat/glow-api-client';
import { storage } from '@innovamat/radiance-utils';
import { useWebviewBridge } from '@innovamat/webview-bridge';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';
import { ArrowIcon as ArrowIconComponent } from '../components/arrow-icon';
import {
  AnchorStyled,
  ArrowIconWrapper,
  BottomContainer,
  ButtonAtlas,
  DemoInformation,
  DemoTextWrapper,
  ErrorMessage,
  ErrorMessageContainer,
  FirstGradient,
  FooterContainer,
  FooterLinks,
  KnowMore,
  LoginCard,
  LoginContainer,
  LoginTitle,
  SecondGradient,
  TopContainer,
} from './login.styled';
import { useDataCenter } from './use-datacenter';

function Login(): JSX.Element {
  const { isInWebview, sendTokenToWebview } = useWebviewBridge();
  const { t } = useTranslation();
  const { onLogin, notification, isLoading, handleIsUsa } = useLogin(
    false,
    true
  );

  const { mutate } = useGetAtlasDemoStudentEmailMutation();

  const { isLoggedIn } = useUser();

  const [showError, setShowError] = useState(false);
  const [isButtonLoading, setIsButtonLoading] = useState(false);

  const handleLogin = (): void => {
    setIsButtonLoading(true);

    setShowError(false);

    mutate(
      {},
      {
        onSuccess(data) {
          const email = data.getAtlasDemoStudentEmail;

          if (!email) {
            setShowError(true);
            return;
          }

          onLogin({
            type: 'users',
            payload: {
              username: email,
              password: 'KMu7l58k@HPqHVhA',
            },
          });
          localStorage.removeItem('sso_clicked');
        },
        onError() {
          setShowError(true);
        },
        onSettled() {
          setIsButtonLoading(false);
        },
      }
    );
  };

  const { handleSelectLanguage } = useDataCenter({ handleIsUsa });

  // TODO: Remove this temporary fix to set the language to the device language.
  // We should manage this using our monorepo libraries and getting the list from the localization library
  useEffect(() => {
    handleSelectLanguage('en_US');
  }, []);

  if (isLoggedIn) {
    if (isInWebview) {
      const tokens = storage.tokenInfo.get();
      if (tokens) {
        const ssoClicked = localStorage.getItem('sso_clicked');
        sendTokenToWebview({
          ...tokens,
          needsPasswordToLogout: ssoClicked !== 'true',
        });
        localStorage.removeItem('sso_clicked');
      }
    }
    return <Navigate to={`/home`} replace />;
  }

  return (
    <>
      <LoginContainer>
        <FirstGradient />
        <SecondGradient />
        <LoginCard>
          <DemoTextWrapper>
            <TopContainer>
              <LoginTitle>{t('login.demo.accessToAtlasMathDemo')}®</LoginTitle>
              <DemoInformation>{t('login.demo.alertMessage')}</DemoInformation>
            </TopContainer>
            <BottomContainer>
              <ButtonAtlas
                data-cy="loginButton"
                data-testid="loginButton"
                onClick={handleLogin}
                loading={isLoading || isButtonLoading}
                disabled={isLoading || isButtonLoading}
              >
                {t('login.demo.loginDemo')}
                <ArrowIconWrapper>
                  <ArrowIconComponent />
                </ArrowIconWrapper>
              </ButtonAtlas>
              {(notification || showError) && (
                <ErrorMessageContainer>
                  <ErrorMessage>
                    {t('login.demo.noAccountAvailable')}
                  </ErrorMessage>
                </ErrorMessageContainer>
              )}
              <KnowMore>
                <span>{t('Login.knowMoreAboutInnovamat')}</span>
                &nbsp;
                <AnchorStyled
                  href={t('login.demo.reviewersLanding')}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="knowMore"
                >
                  {t('Login.clickHere')}
                </AnchorStyled>
              </KnowMore>
            </BottomContainer>
          </DemoTextWrapper>
          <FooterContainer>
            <FooterLinks>
              <AnchorStyled
                href={t('login.privacyPolicy.link')}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="knowMore"
              >
                {t('login.privacyPolicy')}
              </AnchorStyled>
              <AnchorStyled
                href={t('login.termsOfService.link')}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="knowMore"
              >
                {t('login.termsOfService')}
              </AnchorStyled>
            </FooterLinks>
          </FooterContainer>
        </LoginCard>
      </LoginContainer>
    </>
  );
}

export default Login;
